import { array, boolean, discriminatedUnion, enum as enumFactory, iso, literal, number, record, strictObject, string, url } from "../../lazy-zod.js";
import { openMeldRequestContextSchema } from "../daemon/profile-identity.js";
import { centerAgentToolSpeakerSchema } from "./center-agent-speaker.js";
//#region src/schemas/http/organization-skills.ts
const ORGANIZATION_SKILL_NAME_MAX_CHARS = 64;
const ORGANIZATION_SKILL_DESCRIPTION_MAX_CHARS = 1024;
const ORGANIZATION_SKILL_INSTRUCTIONS_MAX_CHARS = 1e5;
const ORGANIZATION_SKILL_FILE_MAX_BYTES = 256e3;
const ORGANIZATION_SKILL_BUNDLE_MAX_BYTES = 2e6;
const ORGANIZATION_SKILL_FILE_LIMIT = 128;
const ORGANIZATION_SKILL_RUNTIME_RELEASE_LIMIT = 64;
const ORGANIZATION_SKILL_RUNTIME_CATALOG_MAX_BYTES = 16e6;
const ORGANIZATION_SKILL_OPERATION_ID_MAX_CHARS = 256;
const ORGANIZATION_SKILL_ERROR_CODES = [
	"organization_skill.bundle_too_large",
	"organization_skill.edit_forbidden",
	"organization_skill.file_limit_reached",
	"organization_skill.frontmatter_invalid",
	"organization_skill.github_response_invalid",
	"organization_skill.github_selection_required",
	"organization_skill.github_source_changed",
	"organization_skill.github_tree_too_large",
	"organization_skill.github_unavailable",
	"organization_skill.github_url_invalid",
	"organization_skill.human_actor_required",
	"organization_skill.invalid_bundle",
	"organization_skill.maintainer_not_member",
	"organization_skill.manage_forbidden",
	"organization_skill.name_exists",
	"organization_skill.not_found",
	"organization_skill.operation_conflict",
	"organization_skill.operation_in_progress",
	"organization_skill.organization_context_changed",
	"organization_skill.release_digest_mismatch",
	"organization_skill.release_invalid",
	"organization_skill.release_missing",
	"organization_skill.release_object_missing",
	"organization_skill.runtime_catalog_too_large",
	"organization_skill.runtime_release_limit",
	"organization_skill.skill_markdown_invalid",
	"organization_skill.skill_markdown_missing",
	"organization_skill.store_unavailable",
	"organization_skill.unsafe_bundle",
	"organization_skill.version_conflict"
];
const identifierSchema = string().trim().min(1).max(256);
const isoDateTimeSchema = iso.datetime({ offset: true });
const digestSchema = string().regex(/^[a-f0-9]{64}$/);
const skillNameSchema = string().trim().min(1).max(64).regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "Skill names use lowercase letters, numbers, and single hyphens.");
const descriptionSchema = string().trim().min(1).max(ORGANIZATION_SKILL_DESCRIPTION_MAX_CHARS);
const instructionsSchema = string().max(ORGANIZATION_SKILL_INSTRUCTIONS_MAX_CHARS);
const sourceUrlSchema = url().max(2048);
const operationIdSchema = string().trim().min(1).max(256);
const metadataSchema = record(string().trim().min(1).max(128), string().max(1024));
const organizationSkillGitHubSourceSchema = strictObject({
	commitSha: string().regex(/^[a-f0-9]{40}$/),
	kind: literal("github"),
	path: string().max(1024),
	repository: sourceUrlSchema
});
const organizationSkillSourceSchema = discriminatedUnion("kind", [strictObject({ kind: literal("created") }), organizationSkillGitHubSourceSchema]);
const organizationSkillPortableMetadataSchema = strictObject({
	allowedTools: string().max(2048).nullable(),
	compatibility: string().max(500).nullable(),
	description: descriptionSchema,
	license: string().max(1024).nullable(),
	metadata: metadataSchema,
	name: skillNameSchema
});
const organizationSkillFileSchema = strictObject({
	content: string().max(ORGANIZATION_SKILL_FILE_MAX_BYTES),
	encoding: enumFactory(["base64", "utf8"]),
	mediaType: string().trim().min(1).max(256),
	path: string().trim().min(1).max(1024).refine(isSafePortableSkillPath, "Skill file path is not safe."),
	size: number().int().nonnegative().max(ORGANIZATION_SKILL_FILE_MAX_BYTES)
});
const organizationSkillBundleSchema = strictObject({
	files: array(organizationSkillFileSchema).max(128),
	instructions: instructionsSchema,
	portable: organizationSkillPortableMetadataSchema
}).superRefine((bundle, context) => {
	const normalizedPaths = /* @__PURE__ */ new Set();
	let totalSize = new TextEncoder().encode(bundle.instructions).byteLength;
	for (const [index, file] of bundle.files.entries()) {
		const normalized = file.path.normalize("NFC");
		if (normalizedPaths.has(normalized)) context.addIssue({
			code: "custom",
			message: "Skill file paths must be unique after normalization.",
			path: [
				"files",
				index,
				"path"
			]
		});
		if (normalized === "SKILL.md") context.addIssue({
			code: "custom",
			message: "SKILL.md is represented by the portable Skill fields.",
			path: [
				"files",
				index,
				"path"
			]
		});
		normalizedPaths.add(normalized);
		const actualSize = readOrganizationSkillFileSize(file);
		if (actualSize === null) {
			context.addIssue({
				code: "custom",
				message: "Skill file content is not valid base64.",
				path: [
					"files",
					index,
					"content"
				]
			});
			continue;
		}
		if (actualSize !== file.size) context.addIssue({
			code: "custom",
			message: "Skill file size does not match its content.",
			path: [
				"files",
				index,
				"size"
			]
		});
		if (actualSize > 256e3) context.addIssue({
			code: "custom",
			message: "Skill file is too large.",
			path: ["files", index]
		});
		totalSize += actualSize;
	}
	if (totalSize > 2e6) context.addIssue({
		code: "custom",
		message: "Skill bundle is too large.",
		path: ["files"]
	});
});
const organizationSkillReleaseSummarySchema = strictObject({
	createdAt: isoDateTimeSchema,
	createdByUserId: identifierSchema,
	digest: digestSchema,
	id: identifierSchema,
	source: organizationSkillSourceSchema
});
const organizationSkillPermissionsSchema = strictObject({
	canEdit: boolean(),
	canManage: boolean()
});
const organizationSkillSummarySchema = strictObject({
	activeRelease: organizationSkillReleaseSummarySchema,
	createdAt: isoDateTimeSchema,
	description: descriptionSchema,
	enabled: boolean(),
	id: identifierSchema,
	name: skillNameSchema,
	organizationId: identifierSchema,
	permissions: organizationSkillPermissionsSchema,
	source: organizationSkillSourceSchema,
	updatedAt: isoDateTimeSchema,
	version: number().int().positive()
});
const organizationSkillSchema = organizationSkillSummarySchema.extend({
	bundle: organizationSkillBundleSchema,
	maintainerUserIds: array(identifierSchema),
	releases: array(organizationSkillReleaseSummarySchema)
});
const listOrganizationSkillsQuerySchema = strictObject({});
const listOrganizationSkillsResponseSchema = strictObject({
	canManage: boolean(),
	organizationId: identifierSchema,
	skills: array(organizationSkillSummarySchema)
});
const organizationSkillRouteParamsSchema = strictObject({ skillId: identifierSchema });
const organizationSkillRuntimeReleaseSchema = strictObject({
	bundle: organizationSkillBundleSchema,
	digest: digestSchema,
	releaseId: identifierSchema,
	skillId: identifierSchema
});
const getOrganizationSkillResponseSchema = strictObject({
	skill: organizationSkillSchema,
	selectedRelease: organizationSkillRuntimeReleaseSchema.optional()
});
const createOrganizationSkillBodySchema = strictObject({
	description: descriptionSchema,
	expectedOrganizationId: identifierSchema,
	instructions: instructionsSchema,
	name: skillNameSchema
});
const createOrganizationSkillResponseSchema = strictObject({ skill: organizationSkillSchema });
const updateOrganizationSkillBodySchema = strictObject({
	bundle: organizationSkillBundleSchema,
	expectedActiveReleaseId: identifierSchema,
	expectedOrganizationId: identifierSchema,
	expectedVersion: number().int().positive()
});
const updateOrganizationSkillResponseSchema = strictObject({ skill: organizationSkillSchema });
const setOrganizationSkillEnabledBodySchema = strictObject({
	enabled: boolean(),
	expectedOrganizationId: identifierSchema,
	expectedVersion: number().int().positive()
});
const setOrganizationSkillEnabledResponseSchema = strictObject({ skill: organizationSkillSchema });
const setOrganizationSkillMaintainersBodySchema = strictObject({
	expectedOrganizationId: identifierSchema,
	expectedVersion: number().int().positive(),
	maintainerUserIds: array(identifierSchema).max(100)
});
const setOrganizationSkillMaintainersResponseSchema = strictObject({ skill: organizationSkillSchema });
const deleteOrganizationSkillBodySchema = strictObject({
	expectedOrganizationId: identifierSchema,
	expectedVersion: number().int().positive()
});
const deleteOrganizationSkillResponseSchema = strictObject({
	deleted: literal(true),
	organizationId: identifierSchema,
	skillId: identifierSchema
});
const importOrganizationSkillFromGitHubBodySchema = strictObject({
	expectedCommitSha: string().regex(/^[a-f0-9]{40}$/).optional(),
	expectedOrganizationId: identifierSchema,
	url: sourceUrlSchema
});
const importOrganizationSkillFromGitHubResponseSchema = strictObject({ skill: organizationSkillSchema });
const previewOrganizationSkillFromGitHubBodySchema = strictObject({
	expectedOrganizationId: identifierSchema,
	url: sourceUrlSchema
});
const organizationSkillGitHubPreviewSchema = strictObject({
	files: array(organizationSkillFileSchema.pick({
		encoding: true,
		mediaType: true,
		path: true,
		size: true
	})),
	instructionsCharacters: number().int().nonnegative(),
	portable: organizationSkillPortableMetadataSchema,
	source: organizationSkillGitHubSourceSchema,
	warnings: array(string().trim().min(1).max(1024))
});
const previewOrganizationSkillFromGitHubResponseSchema = strictObject({
	organizationId: identifierSchema,
	preview: organizationSkillGitHubPreviewSchema
});
const centerAgentReadOrganizationSkillsRpcInputSchema = discriminatedUnion("action", [
	strictObject({
		action: literal("list"),
		context: openMeldRequestContextSchema,
		speakerMember: centerAgentToolSpeakerSchema
	}),
	strictObject({
		action: literal("get"),
		context: openMeldRequestContextSchema,
		skillId: identifierSchema,
		speakerMember: centerAgentToolSpeakerSchema
	}),
	strictObject({
		action: literal("preview_github"),
		context: openMeldRequestContextSchema,
		speakerMember: centerAgentToolSpeakerSchema,
		url: sourceUrlSchema
	})
]);
const centerAgentReadOrganizationSkillsResponseSchema = discriminatedUnion("action", [
	strictObject({
		action: literal("list"),
		canManage: boolean(),
		organizationId: identifierSchema,
		skills: array(organizationSkillSummarySchema)
	}),
	strictObject({
		action: literal("get"),
		organizationId: identifierSchema,
		skill: organizationSkillSchema
	}),
	strictObject({
		action: literal("preview_github"),
		organizationId: identifierSchema,
		preview: organizationSkillGitHubPreviewSchema
	})
]);
const centerAgentInstallOrganizationSkillRpcInputSchema = discriminatedUnion("action", [strictObject({
	action: literal("create"),
	bundle: organizationSkillBundleSchema,
	context: openMeldRequestContextSchema,
	operationId: operationIdSchema,
	speakerMember: centerAgentToolSpeakerSchema
}), strictObject({
	action: literal("import_github"),
	context: openMeldRequestContextSchema,
	expectedCommitSha: string().regex(/^[a-f0-9]{40}$/),
	operationId: operationIdSchema,
	speakerMember: centerAgentToolSpeakerSchema,
	url: sourceUrlSchema
})]);
const centerAgentInstallOrganizationSkillResponseSchema = discriminatedUnion("action", [strictObject({
	action: literal("create"),
	organizationId: identifierSchema,
	skill: organizationSkillSummarySchema
}), strictObject({
	action: literal("import_github"),
	organizationId: identifierSchema,
	skill: organizationSkillSummarySchema
})]);
const organizationSkillRuntimeReleaseSelectionSchema = strictObject({
	digest: digestSchema,
	name: skillNameSchema,
	releaseId: identifierSchema,
	skillId: identifierSchema
});
const organizationSkillRuntimeSnapshotResponseSchema = strictObject({
	organizationId: identifierSchema,
	releases: array(organizationSkillRuntimeReleaseSchema).max(64)
});
const listOrganizationSkillsRpcInputSchema = strictObject({ context: openMeldRequestContextSchema });
const getOrganizationSkillRpcInputSchema = strictObject({
	context: openMeldRequestContextSchema,
	skillId: identifierSchema,
	releaseId: identifierSchema.optional()
});
const createOrganizationSkillRpcInputSchema = createOrganizationSkillBodySchema.extend({ context: openMeldRequestContextSchema });
const updateOrganizationSkillRpcInputSchema = updateOrganizationSkillBodySchema.extend({
	context: openMeldRequestContextSchema,
	skillId: identifierSchema
});
const setOrganizationSkillEnabledRpcInputSchema = setOrganizationSkillEnabledBodySchema.extend({
	context: openMeldRequestContextSchema,
	skillId: identifierSchema
});
const setOrganizationSkillMaintainersRpcInputSchema = setOrganizationSkillMaintainersBodySchema.extend({
	context: openMeldRequestContextSchema,
	skillId: identifierSchema
});
const deleteOrganizationSkillRpcInputSchema = deleteOrganizationSkillBodySchema.extend({
	context: openMeldRequestContextSchema,
	skillId: identifierSchema
});
const importOrganizationSkillFromGitHubRpcInputSchema = importOrganizationSkillFromGitHubBodySchema.extend({ context: openMeldRequestContextSchema });
const previewOrganizationSkillFromGitHubRpcInputSchema = previewOrganizationSkillFromGitHubBodySchema.extend({ context: openMeldRequestContextSchema });
const readOrganizationSkillRuntimeSnapshotRpcInputSchema = strictObject({
	context: openMeldRequestContextSchema,
	preparationIdempotencyKey: string().min(1).max(2048).optional(),
	releases: array(organizationSkillRuntimeReleaseSelectionSchema).max(64)
});
const recordOrganizationSkillActivationRpcInputSchema = strictObject({
	context: openMeldRequestContextSchema,
	digest: digestSchema,
	releaseId: identifierSchema,
	routineRunId: identifierSchema.nullable(),
	skillId: identifierSchema,
	submissionId: identifierSchema
});
const recordOrganizationSkillActivationResponseSchema = strictObject({ recorded: boolean() });
function isSafePortableSkillPath(value) {
	if (value.startsWith("/") || value.startsWith("\\") || value.includes("\\") || value.includes("\0")) return false;
	return value.split("/").every((segment) => segment.length > 0 && segment !== "." && segment !== "..");
}
function readOrganizationSkillFileSize(file) {
	if (file.encoding === "utf8") return new TextEncoder().encode(file.content).byteLength;
	try {
		return atob(file.content).length;
	} catch {
		return null;
	}
}
function stringifyOrganizationSkillBundleCanonicalJson(bundle) {
	return JSON.stringify(sortOrganizationSkillJson(bundle));
}
function sortOrganizationSkillJson(value) {
	if (Array.isArray(value)) return value.map(sortOrganizationSkillJson);
	if (value && typeof value === "object") return Object.fromEntries(Object.entries(value).sort(([left], [right]) => compareOrganizationSkillJsonKeys(left, right)).map(([key, item]) => [key, sortOrganizationSkillJson(item)]));
	return value;
}
function compareOrganizationSkillJsonKeys(left, right) {
	if (left < right) return -1;
	if (left > right) return 1;
	return 0;
}
//#endregion
export { ORGANIZATION_SKILL_BUNDLE_MAX_BYTES, ORGANIZATION_SKILL_DESCRIPTION_MAX_CHARS, ORGANIZATION_SKILL_ERROR_CODES, ORGANIZATION_SKILL_FILE_LIMIT, ORGANIZATION_SKILL_FILE_MAX_BYTES, ORGANIZATION_SKILL_INSTRUCTIONS_MAX_CHARS, ORGANIZATION_SKILL_NAME_MAX_CHARS, ORGANIZATION_SKILL_OPERATION_ID_MAX_CHARS, ORGANIZATION_SKILL_RUNTIME_CATALOG_MAX_BYTES, ORGANIZATION_SKILL_RUNTIME_RELEASE_LIMIT, centerAgentInstallOrganizationSkillResponseSchema, centerAgentInstallOrganizationSkillRpcInputSchema, centerAgentReadOrganizationSkillsResponseSchema, centerAgentReadOrganizationSkillsRpcInputSchema, createOrganizationSkillBodySchema, createOrganizationSkillResponseSchema, createOrganizationSkillRpcInputSchema, deleteOrganizationSkillBodySchema, deleteOrganizationSkillResponseSchema, deleteOrganizationSkillRpcInputSchema, getOrganizationSkillResponseSchema, getOrganizationSkillRpcInputSchema, importOrganizationSkillFromGitHubBodySchema, importOrganizationSkillFromGitHubResponseSchema, importOrganizationSkillFromGitHubRpcInputSchema, listOrganizationSkillsQuerySchema, listOrganizationSkillsResponseSchema, listOrganizationSkillsRpcInputSchema, organizationSkillBundleSchema, organizationSkillFileSchema, organizationSkillGitHubPreviewSchema, organizationSkillGitHubSourceSchema, organizationSkillPermissionsSchema, organizationSkillPortableMetadataSchema, organizationSkillReleaseSummarySchema, organizationSkillRouteParamsSchema, organizationSkillRuntimeReleaseSchema, organizationSkillRuntimeReleaseSelectionSchema, organizationSkillRuntimeSnapshotResponseSchema, organizationSkillSchema, organizationSkillSourceSchema, organizationSkillSummarySchema, previewOrganizationSkillFromGitHubBodySchema, previewOrganizationSkillFromGitHubResponseSchema, previewOrganizationSkillFromGitHubRpcInputSchema, readOrganizationSkillRuntimeSnapshotRpcInputSchema, recordOrganizationSkillActivationResponseSchema, recordOrganizationSkillActivationRpcInputSchema, setOrganizationSkillEnabledBodySchema, setOrganizationSkillEnabledResponseSchema, setOrganizationSkillEnabledRpcInputSchema, setOrganizationSkillMaintainersBodySchema, setOrganizationSkillMaintainersResponseSchema, setOrganizationSkillMaintainersRpcInputSchema, stringifyOrganizationSkillBundleCanonicalJson, updateOrganizationSkillBodySchema, updateOrganizationSkillResponseSchema, updateOrganizationSkillRpcInputSchema };

//# sourceMappingURL=organization-skills.js.map